import React, { useLayoutEffect, useRef, useState, useContext, useEffect } from 'react'
import { Rnd } from 'react-rnd'
import FontFaceObserver from 'fontfaceobserver'

import PropTypes from 'prop-types'
import { GlobalContext } from 'core/contexts/global-context'

import Titlebar from 'core/components/Titlebar'

const DynamicWindow = props => {
  if (!props.open) return null

  const [position, setPosition] = useState({ x: null, y: null })
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const { screenWidth, screenHeight, mobile } = useContext(GlobalContext)
  const ref = useRef(null)

  /**
   * This effect will fire when things change between mobile and not mobile.
   * So we can use this to retrigger the window, for example.
   */
  useEffect(() => {}, [mobile])

  // Window layout will break below 150px.
  const minWidth = props.minWidth > 150 ? props.minWidth : 150
  const minHeight = props.minWidth > 150 ? props.minHeight : 150

  useLayoutEffect(() => {
    const offset = 185

    let x, y
    let width = props.width ? props.width : Math.floor(0.8 * screenWidth)
    let height = props.height ? props.height : Math.floor(0.66 * screenHeight)

    if (width !== 'auto' && props.maxWidth < width) width = props.maxWidth
    if (width !== 'auto' && props.maxHeight < height) height = props.maxHeight
    const padding = global.mobile ? 15 : 30

    setDimensions(() => ({ width: width, height: height }))

    if (typeof props.x === 'string') {
      if (props.x.includes('%')) {
        x = Math.floor(screenWidth * (parseInt(props.x) / 100))
      } else {
        x = parseInt(props.x)
      }
    } else if (!props.x) {
      x = Math.floor(screenWidth / 2 - width / 2)
    }
    if (x < padding) x = padding

    if (typeof props.y === 'string') {
      if (props.y.includes('%')) {
        y = Math.floor((screenHeight - offset) * (parseInt(props.y) / 100))
      } else {
        y = parseInt(props.y)
      }
    } else if (!props.y) {
      y = Math.floor((screenHeight - offset) / 2 - height / 2)
    }

    const font = new FontFaceObserver('Champion Heavyweight')
    font.load().then(() => {
      if (ref.current && ref.current.resizable) {
        const windowWidth = ref.current.resizable.size.width
        const windowHeight = ref.current.resizable.size.height

        if (x + windowWidth >= screenWidth) {
          x -= x + padding + windowWidth - screenWidth
        }
        if (y + windowHeight >= screenHeight) {
          y -= y + offset / 2 + padding + windowHeight - screenHeight
        }

        if (position.x === null && position.y === null) {
          setPosition({ x: x, y: y })
        }
      }

      if (props.onLoad && ref.current) {
        props.onLoad(ref.current.resizable.size)
      }
    })
  }, [])

  /**
   * Mouse down handle that fires when a user mouses down anywhere in a dynamic window
   * (right now it's attached to the .window-wrapper element). Right now, we're
   * using it exclusively for setting the windows z-index and bringing the focused
   * window to the front.
   */
  const handleMouseDown = e => {
    const current = e.currentTarget.parentElement
    const allWrappers = document.querySelectorAll('.window-wrapper')

    if (parseInt(current.style.zIndex, 10) === allWrappers.length) return

    for (let i = 0; i < allWrappers.length; i++) {
      const dynamicWindow = allWrappers[i].parentElement

      const z = dynamicWindow.style.zIndex
      if (!z) continue
      dynamicWindow.style.zIndex = z > 0 ? z - 1 : null
    }

    current.style.zIndex = allWrappers.length
  }

  return (
    <Rnd
      bounds={props.bounds}
      ref={ref}
      className="window"
      size={{ width: dimensions.width, height: dimensions.height }}
      onResizeStop={(e, direction, ref, delta, position) => {
        const width = ref.style.width
        const height = ref.style.height
        setDimensions({ width, height })
        if (props.onResizeStop) {
          props.onResizeStop({ width: ref.style.width })
        }
      }}
      onResize={(e, direction, ref, delta, position) => {
        if (props.onResize) {
          props.onResize({ width: ref.style.width })
        }
      }}
      position={{ x: position.x, y: position.y }}
      onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
      maxWidth={props.maxWidth}
      maxHeight={props.maxHeight}
      minWidth={minWidth}
      minHeight={minHeight}
      enableResizing={{
        bottomRight: props.resizable & !props.mobile ? props.resizable : false
      }}
      dragHandleClassName="window-titlebar"
      resizeHandleClasses={{ bottomRight: 'window-resize' }}
    >
      <div className="window-wrapper" onMouseDown={handleMouseDown}>
        {props.titlebar && <Titlebar {...props.titlebar} />}
        <div className={`window-content mod-${props.titlebar ? 'has-titlebar' : 'no-titlebar'}`}>
          {props.loading ? <div className="window-loading">Loading...</div> : props.children}
        </div>
      </div>
    </Rnd>
  )
}

/**
 * Window Component.
 *
 * mobile     boolean           Use mobile defaults.
 * open       boolean           Current Window visibility state.
 * resizable  boolean           Show resize handle.
 * x          number            Window x-axis position by % or px.  Default: Centered.
 * y          number            Window y-axis position by % or px.  Default: Centered.
 * width      number || string  Window width.                       Default: 80% of parent.
 * height     number || string  Window height.                      Default: 66% of parent.
 * minWidth   number || string  Window min-width.                   Default: 150px.
 * minHeight  number || string  Window min-height.                  Default: 150px.
 * maxWidth   number || string  Window max-width.                   Default: 100% of parent.
 * maxHeight  number || string  Window max-height.                  Default: 100% of parent.
 */

DynamicWindow.defaultProps = {
  minWidth: 150,
  minHeight: 150,
  maxWidth: '100%',
  maxHeight: '100%',
  bounds: 'parent'
}

DynamicWindow.propTypes = {
  bounds: PropTypes.string,
  resizable: PropTypes.bool,
  onResize: PropTypes.func,
  onResizeStop: PropTypes.func,
  onLoad: PropTypes.func,
  mobile: PropTypes.bool,
  open: PropTypes.bool,
  titlebar: PropTypes.object,
  x: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

export default DynamicWindow
